import React, {useState, useEffect, useMemo} from 'react';
import {Title} from "react-admin";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import CashflowSettings from "./CashflowSettings";
import CashflowTable from "./CashflowTable";
import CashflowSummary from "./CashflowSummary";
import {useCashflowCalculations} from "./useCashflowCalculations";
import {getCashflow} from "../../../utils/api/custom-request";

/**
 * Main Cashflow page component
 * Displays cashflow table with editable fields and real-time calculations
 */
const Cashflow = () => {
  // API data state
  const [rawData, setRawData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User settings state
  const [settings, setSettings] = useState({
    startingCapital: 0,
    monthlyExpenses: 0
  });

  // Date range filter
  const [dateRange, setDateRange] = useState({startMonth: '', endMonth: ''});

  // Available months derived from initial unfiltered load
  const [availableMonths, setAvailableMonths] = useState([]);

  // User overrides for sales/expenses (by month)
  const [overrides, setOverrides] = useState({});

  // Manually added months
  const [additionalMonths, setAdditionalMonths] = useState([]);

  // Merge API data with manually added months
  const allData = useMemo(() => {
    const combined = [...rawData];
    
    additionalMonths.forEach(addedMonth => {
      const exists = combined.some(row => row.month === addedMonth.month);
      if (!exists) {
        combined.push(addedMonth);
      }
    });
    
    return combined.sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [rawData, additionalMonths]);

  // Calculate rows and totals using custom hook
  const {rows, totals} = useCashflowCalculations(allData, settings, overrides);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        if (dateRange.startMonth) {
          params.startDate = `${dateRange.startMonth}-01`;
        }
        if (dateRange.endMonth) {
          // Get the last day of the selected month
          const [year, month] = dateRange.endMonth.split('-').map(Number);
          const lastDay = new Date(year, month, 0).getDate();
          params.endDate = `${dateRange.endMonth}-${String(lastDay).padStart(2, '0')}`;
        }

        const response = await getCashflow(params);

        if (response.data) {
          const rows = response.data.rows || [];
          setRawData(rows);
          setSummary(response.data.summary || {});
          // Populate available months from unfiltered data (first load)
          if (!dateRange.startMonth && !dateRange.endMonth) {
            setAvailableMonths(rows.map(r => r.month).sort());
          }
        }
      } catch (err) {
        console.error('Failed to fetch cashflow data:', err);
        setError('Failed to load cashflow data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  // Handle override changes from table
  const handleOverrideChange = (month, field, value) => {
    setOverrides(prev => ({
      ...prev,
      [month]: {
        ...prev[month],
        [field]: value
      }
    }));
  };

  // Handle settings changes
  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
  };

  // Handle date range changes
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setOverrides({});
    setAdditionalMonths([]);
  };

  // Handle adding new month
  const handleAddMonth = (monthKey, position) => {
    const newMonth = {
      month: monthKey,
      sales: 0,
      total_income: 0,
      total_principal: 0,
      total_interest: 0,
      loans_count: 0,
      paid_count: 0,
      waiting_count: 0,
      month_type: 'forecast',
      is_manual: true
    };
    
    setAdditionalMonths(prev => [...prev, newMonth]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{p: 2, margin: '0 auto'}}>
      <Title title="Cashflow" />
      
      {/* Header */}
      <Box sx={{mb: 3}}>
        <Typography variant="h5" gutterBottom>
          Cashflow
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Settings Panel */}
      <CashflowSettings
        settings={settings}
        onSettingsChange={handleSettingsChange}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        availableMonths={availableMonths}
      />

      {/* Main Table */}
      {rows.length > 0 ? (
        <CashflowTable
          rows={rows}
          totals={totals}
          onOverrideChange={handleOverrideChange}
          onAddMonth={handleAddMonth}
        />
      ) : (
        <Card sx={{p: 4, textAlign: 'center'}}>
          <Typography color="textSecondary">
            No cashflow data available. Create some loans to see the cashflow projection.
          </Typography>
        </Card>
      )}

      {/* Summary Cards */}
      <CashflowSummary
        totals={totals}
        summary={summary}
      />
    </Box>
  );
};

export default Cashflow;